/**
 * Route prefetch composable - prefetches route chunks on hover/focus
 * Improves navigation speed by loading chunks before user clicks
 */

type PrefetchFn = () => Promise<unknown>

const prefetched = new Set<string>()

/**
 * Prefetch a route chunk. Safe to call multiple times - only fetches once.
 */
export function prefetchRoute(importFn: PrefetchFn, routeName: string) {
  if (prefetched.has(routeName)) return
  prefetched.add(routeName)
  importFn().catch(() => {
    // Silently ignore prefetch failures (network issues, etc.)
    prefetched.delete(routeName)
  })
}

/**
 * Setup prefetch on hover/focus for a link element
 */
export function setupPrefetch(
  element: HTMLElement,
  importFn: PrefetchFn,
  routeName: string
) {
  const handler = () => prefetchRoute(importFn, routeName)

  element.addEventListener('mouseenter', handler, { once: true, passive: true })
  element.addEventListener('focus', handler, { once: true, passive: true })

  return () => {
    element.removeEventListener('mouseenter', handler)
    element.removeEventListener('focus', handler)
  }
}

/**
 * Prefetch critical routes after initial load (when browser is idle)
 */
export function prefetchCriticalRoutes(routes: Array<{ importFn: PrefetchFn; name: string }>) {
  if (typeof window === 'undefined') return
  if (!('requestIdleCallback' in window)) {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      routes.forEach(({ importFn, name }) => prefetchRoute(importFn, name))
    }, 2000)
    return
  }

  requestIdleCallback(
    () => {
      routes.forEach(({ importFn, name }) => prefetchRoute(importFn, name))
    },
    { timeout: 3000 }
  )
}
